import React, { useState } from 'react';
import { MsFormulaire } from '../types';
import {
  createSection,
  deleteSection,
  createArticle,
  deleteArticle,
} from '../services/operationMsService';
import './OperationCreationArticle.css';
import { ReactComponent as CrossIcon } from '../../../assets/icons/cross.svg';


// Fork acheteur-MS (V2) de CreationArticle : descriptif financier (DPGF).
// CRUD sections/articles via le service id-based. L'operationId est recu en PROP
// (suppression du sniffing window.location de l'oracle). Aucune branche role/type.

interface Article {
  id: string;
  nom: string;
  code: number;
  [key: string]: unknown;
}

interface Section {
  id: string;
  name: string;
  articles: Article[];
  [key: string]: unknown;
}

interface OperationCreationArticleProps {
  operationId: number | string;
  formData: MsFormulaire;
  setFormData?: (data: MsFormulaire) => void;
  viewMode?: boolean;
}

// Les sections du buffer formulaire arrivent en `unknown[]` (cf. MsFormulaire) :
// on les normalise vers la forme locale {id, name, articles[]} consommee par la vue.
const asSection = (raw: unknown): Section => {
  const s = (raw ?? {}) as { id?: unknown; name?: unknown; articles?: unknown[] };
  return {
    id: String(s.id ?? ''),
    name: String(s.name ?? ''),
    articles: Array.isArray(s.articles)
      ? s.articles.map((a) => {
          const art = (a ?? {}) as { id?: unknown; nom?: unknown; code?: unknown };
          return {
            id: String(art.id ?? ''),
            nom: String(art.nom ?? ''),
            code: Number(art.code ?? 0),
          };
        })
      : [],
  };
};

const OperationCreationArticle: React.FC<OperationCreationArticleProps> = ({
  operationId,
  formData,
  setFormData,
  viewMode = false,
}) => {
  const [newSectionName, setNewSectionName] = useState('');
  const [newArticleName, setNewArticleName] = useState('');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [loadingSectionId, setLoadingSectionId] = useState<string | null>(null);
  const [loadingArticleId, setLoadingArticleId] = useState<string | null>(null);
  const [isAddingSectionLoading, setIsAddingSectionLoading] = useState(false);

  const sections: Section[] = (formData.sections || []).map(asSection);

  // Fait disparaitre les messages apres 2 secondes (succes ou erreur).
  const clearMessageAfterDelay = (messageType: 'success' | 'error') => {
    setTimeout(() => {
      if (messageType === 'success') {
        setSuccessMsg('');
      } else {
        setError(null);
      }
    }, 2000);
  };

  // Ajout d'une section avec appel API.
  const handleAddSection = async () => {
    if (!viewMode && setFormData && newSectionName) {
      setIsAddingSectionLoading(true);
      setError(null);
      setSuccessMsg('');
      try {
        const data = await createSection(operationId, newSectionName);
        const newSection: Section = {
          id: String(data.data?.id ?? ''),
          name: data.data?.nom || newSectionName,
          articles: [],
        };
        setFormData({
          ...formData,
          sections: [...sections, newSection],
        });
        setNewSectionName('');
        setActiveSection(newSection.id);
        setSuccessMsg('Section ajoutée !');
        clearMessageAfterDelay('success');
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur lors de l'ajout de la section");
        clearMessageAfterDelay('error');
      } finally {
        setIsAddingSectionLoading(false);
      }
    }
  };

  // Ajout d'un article avec appel API.
  const handleAddArticle = async (sectionId: string) => {
    if (!viewMode && setFormData && newArticleName) {
      if (loadingArticleId === sectionId) return; // empeche le spam
      setLoadingArticleId(sectionId);
      setError(null);
      setSuccessMsg('');

      try {
        // Calcul du prochain numero de code (max global + 1).
        const allCodes = sections.flatMap((section) => section.articles.map((article) => article.code));
        const nextCode = allCodes.length > 0 ? Math.max(...allCodes.map((n) => Number(n) || 0)) + 1 : 1;

        const data = await createArticle(operationId, {
          nom: newArticleName,
          code: nextCode,
          id_section: sectionId,
        });

        const newArticle: Article = {
          id: data.data?.id?.toString() || Date.now().toString(),
          nom: data.data?.nom || newArticleName,
          code: data.data?.code || nextCode,
        };

        setFormData({
          ...formData,
          sections: sections.map((section) =>
            section.id === sectionId
              ? { ...section, articles: [...section.articles, newArticle] }
              : section,
          ),
        });
        setNewArticleName('');
        setSuccessMsg('Article ajouté !');
        clearMessageAfterDelay('success');
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur lors de l'ajout de l'article");
        clearMessageAfterDelay('error');
      } finally {
        setLoadingArticleId(null);
      }
    }
  };

  // Suppression d'une section avec appel API.
  const handleDeleteSection = async (sectionId: string) => {
    if (!viewMode && setFormData) {
      setLoadingSectionId(sectionId);
      setError(null);
      setSuccessMsg('');
      try {
        await deleteSection(operationId, sectionId);
        setFormData({
          ...formData,
          sections: sections.filter((section) => section.id !== sectionId),
        });
        setSuccessMsg('Section supprimée !');
        clearMessageAfterDelay('success');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur lors de la suppression de la section');
        clearMessageAfterDelay('error');
      } finally {
        setLoadingSectionId(null);
      }
    }
  };

  // Suppression d'un article avec appel API.
  const handleDeleteArticle = async (sectionId: string, articleId: string) => {
    if (!viewMode && setFormData) {
      setLoadingArticleId(`${sectionId}-${articleId}`);
      setError(null);
      setSuccessMsg('');
      try {
        await deleteArticle(operationId, articleId);
        setFormData({
          ...formData,
          sections: sections.map((section) =>
            section.id === sectionId
              ? { ...section, articles: section.articles.filter((article) => article.id !== articleId) }
              : section,
          ),
        });
        setSuccessMsg('Article supprimé !');
        clearMessageAfterDelay('success');
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur lors de la suppression de l'article");
        clearMessageAfterDelay('error');
      } finally {
        setLoadingArticleId(null);
      }
    }
  };

  // En consultation sans section : message vide.
  if (viewMode && (!sections || sections.length === 0)) {
    return (
      <div className="empty-sections-message">
        <p>Aucune section financière n'est disponible pour cette commande.</p>
      </div>
    );
  }

  const handleArticleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, sectionId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddArticle(sectionId);
    }
  };

  const handleSectionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSection();
    }
  };

  return (
    <div className={`step-container ${viewMode ? 'view-mode' : ''}`}>
      {!viewMode && (
        <>
          <h4>
            Détail du Prix Global et Forfaitaire (DPGF)<span style={{ color: 'red' }}>*</span>:
          </h4>
          {/* <p style={{ marginTop: '10px', marginBottom: '15px', fontSize: '14px', lineHeight: '1.5', color: '#555' }}>
            Il vous appartient de créer la trame du DPGF que vous souhaitez faire remplir par les prestataires. Au
            moins une ligne est obligatoire pour pouvoir lancer la consultation.
          </p> */}
        </>
      )}

      {error && !viewMode && <div className="error-message validation-error">{error}</div>}

      {successMsg && !viewMode && <div className="success-message">{successMsg}</div>}

      <div className="sections-container">
        {sections.map((section) => (
          <div key={section.id} className="section">
            <div className="titre-article">
              <h5>{section.name}</h5>
              {!viewMode && (
                <button
                  className={`delete-button ${loadingSectionId === section.id ? 'loading' : ''}`}
                  onClick={() => handleDeleteSection(section.id)}
                  type="button"
                  disabled={loadingSectionId === section.id}
                >
                  {loadingSectionId === section.id ? <div className="button-loader"></div> : <CrossIcon />}
                </button>
              )}
            </div>

            <div className="articles-list">
              <table>
                <thead>
                  <tr>
                    <th className="numero-article">N°</th>
                    <th>Description de la prestation</th>
                    {!viewMode && <th style={{ width: '60px' }}>Forfait HT</th>}
                  </tr>
                </thead>
                <tbody>
                  {section.articles.map((article) => (
                    <tr key={article.id}>
                      <td>{article.code}</td>
                      <td>{article.nom}</td>
                      {!viewMode && (
                        <td>
                          <button
                            className={`delete-button ${
                              loadingArticleId === `${section.id}-${article.id}` ? 'loading' : ''
                            }`}
                            onClick={() => handleDeleteArticle(section.id, article.id)}
                            type="button"
                            disabled={loadingArticleId === `${section.id}-${article.id}`}
                          >
                            {loadingArticleId === `${section.id}-${article.id}` ? (
                              <div className="button-loader"></div>
                            ) : (
                              <CrossIcon />
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {!viewMode &&
                (activeSection === section.id ? (
                  <div className="add-article">
                    <input
                      type="text"
                      placeholder="Nom du nouvel article"
                      value={newArticleName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 255) {
                          setNewArticleName(value);
                          setError(null);
                        } else {
                          setError("Le nom de l'article ne peut pas dépasser 255 caractères.");
                        }
                      }}
                      onKeyDown={(e) => handleArticleKeyDown(e, section.id)}
                      disabled={loadingArticleId === section.id}
                    />
                    <button
                      className={`add-button ${loadingArticleId === section.id ? 'loading' : ''}`}
                      onClick={() => handleAddArticle(section.id)}
                      type="button"
                      disabled={loadingArticleId === section.id}
                    >
                      {loadingArticleId === section.id ? <div className="button-loader"></div> : '+'}
                    </button>
                  </div>
                ) : (
                  <button
                    className="add-article-button"
                    onClick={() => setActiveSection(section.id)}
                    type="button"
                  >
                    Ajouter ou modifier un article
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>

      {!viewMode && (
        <div className="add-section">
          <span>Ajouter un chapitre: </span>
          <input
            type="text"
            placeholder="Nom du chapitre"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            onKeyDown={handleSectionKeyDown}
          />
          <button
            className={`add-button ${isAddingSectionLoading ? 'loading' : ''}`}
            onClick={handleAddSection}
            type="button"
            disabled={isAddingSectionLoading}
          >
            {isAddingSectionLoading ? <div className="button-loader"></div> : '+'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OperationCreationArticle;
